// FLAGS: consent, personal-data
// The app's only PostHog wrapper: init against the relay host, identify, group, capture and reset. Every
// entry point is gated on `isConfigured()` and on the consent helper below.
import posthog from "posthog-js";
import { env } from "@src/env";
import { PostHogUiHost } from "@src/utils/constants";
import { hasConsentFor } from "@src/utils/cookiebot";
import { UtmService } from "@src/services/utm.service";
import { AttributionService } from "@src/services/attribution.service";

// Only environments with a PostHog project key and proxy host configured
// (test, production) send events.
const isConfigured = () =>
  Boolean(env.REACT_APP_POSTHOG_API_KEY && env.REACT_APP_POSTHOG_API_HOST);

// FLAGS: consent
// Fails closed: nothing is sent until Cookiebot has loaded and the visitor has accepted both categories.
const hasConsent = () => hasConsentFor(["marketing", "statistics"]);

type AnalyticsUser = {
  id: string;
  employerId?: string;
  language: string;
  yearOfBirth?: number;
  loginProvider: string;
  signUpToNewsletter: boolean;
  isFamily: boolean;
  createdAt: string;
  isManager: boolean;
  isFocusThemeSet: boolean;
};

// FLAGS: personal-data
// The latest identify made before consent, sent when PostHog starts and emptied on sign-out; captures are dropped.
let pendingIdentify: AnalyticsUser | null = null;

// FLAGS: personal-data
// Stamps the landing URL's `utm_*` values onto every event for the rest of the browser session. App.tsx
// strips them from the address bar on its first render, one render before the consent hook lets PostHog
// start, so the first `$pageview` usually no longer sees them in the URL, and on a logged-in landing never
// does. Without this they reach PostHog only as person properties on identify, which cannot say which visit
// or which event a campaign click led to. Same free text as the person properties below, now on events too.
// Session properties merge, so keys the new link lacks are dropped first: a second campaign clicked in the
// same tab must not inherit the first one's `utm_content`.
const registerUtmForSession = (
  utmParams: Record<string, string> | null,
): void => {
  if (!utmParams) return;
  UtmService.utmKeys
    .filter((key) => !(key in utmParams))
    .forEach((key) => posthog.unregister_for_session(key));
  posthog.register_for_session(utmParams);
};

let consentListenersInitialized = false;

// Set up before PostHog starts as well, so an accept that arrives later starts it from here.
const setupConsentListeners = (onConsent: () => void): void => {
  if (consentListenersInitialized || typeof window === "undefined") return;
  consentListenersInitialized = true;

  const handleConsentChange = () => {
    // eslint-disable-next-line no-underscore-dangle
    const isLoaded = posthog.__loaded;

    if (hasConsent()) {
      onConsent();
    } else if (isLoaded) {
      posthog.opt_out_capturing();
    }
  };

  window.addEventListener("CookiebotOnAccept", handleConsentChange);
  window.addEventListener("CookiebotOnDecline", handleConsentChange);
};

export const posthogService = {
  /**
   * The same consent every capture here waits for. Exposed for the Phase Two register hand-off,
   * whose analytics have no banner of their own (OI-25707).
   */
  hasConsent,

  initialize: (): void => {
    if (!isConfigured()) return;
    if (!hasConsent()) return;

    // eslint-disable-next-line no-underscore-dangle
    if (!posthog.__loaded) {
      posthog.init(env.REACT_APP_POSTHOG_API_KEY, {
        api_host: env.REACT_APP_POSTHOG_API_HOST,
        ui_host: PostHogUiHost,
        capture_pageview: false, // Manual tracking for SPA
        capture_pageleave: false, // Doesn't work properly in SPA
        autocapture: {
          css_selector_allowlist: ["[data-pendo-id]", "[data-analytics-id]"],
        },
      });

      registerUtmForSession(UtmService.getUtmFromSessionStorage());
    }
    // PostHog keeps an opt-out across visits, so an earlier decline would otherwise outlive this consent.
    if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
    setupConsentListeners(posthogService.initialize);

    const pending = pendingIdentify;
    pendingIdentify = null;
    if (pending) posthogService.identifyUser(pending);
  },

  // FLAGS: personal-data
  // Attaches employer, profile language, year of birth, login provider and four account flags to a
  // PostHog person, plus whatever `utm_*` values were on the landing URL — arbitrary query-string text
  // becomes a permanent person property. `posthog.group("company", …)` also puts the user in an
  // employer-wide group. It also attaches the public-event attribution pair when the account came from
  // a public event page (OI-25337); that pair is validated at capture, so it does not widen the
  // arbitrary-text problem above.
  // Called from here on first load and from the auth reducers on every auth state change, which is why
  // a visitor who registers in the tab the CTA opened is identified while their attribution is still in
  // session storage.
  identifyUser: (user: AnalyticsUser): void => {
    if (!isConfigured()) return;
    if (!hasConsent()) {
      pendingIdentify = user;
      setupConsentListeners(posthogService.initialize);
      return;
    }

    posthogService.initialize();

    const utmParams = UtmService.getUtmFromSessionStorage();
    // `initialize` registers the UTMs only when it starts PostHog, which may predate App.tsx saving them, so
    // register them here too, before they are cleared below.
    registerUtmForSession(utmParams);
    // Unlike `utm_*` above, this pair is validated before it is stored (known source, well-formed
    // event id), so it cannot be used to write arbitrary text onto an identified person.
    const attribution = AttributionService.getFromSessionStorage();

    posthog.identify(user.id, {
      employerId: user.employerId,
      profileLanguage: user.language,
      yearOfBirth: user.yearOfBirth,
      loginProvider: user.loginProvider,
      signUpToNewsletter: user.signUpToNewsletter,
      isFamily: user.isFamily,
      createdAt: user.createdAt,
      isManager: user.isManager,
      isFocusThemeSet: user.isFocusThemeSet,
      ...(utmParams || {}),
      // Spread directly — null spreads to nothing, so no empty object literal is needed here.
      ...attribution,
    });

    if (user.employerId) {
      posthog.group("company", user.employerId);
    }

    UtmService.removeUtmFromSessionStorage();
    // Cleared with the UTM params: the attribution belongs to the sign-up it travelled with, and
    // leaving it would credit the event for a later, unrelated account on the same browser session.
    AttributionService.removeFromSessionStorage();
  },

  clearUser: (): void => {
    pendingIdentify = null;
    if (!isConfigured()) return;
    // eslint-disable-next-line no-underscore-dangle
    if (!posthog.__loaded) return;

    posthog.reset();
  },

  capture: (eventName: string, properties?: Record<string, unknown>): void => {
    if (!isConfigured()) return;
    if (!hasConsent()) return;

    posthogService.initialize();
    posthog.capture(eventName, properties);
  },

  trackPageView: (): void => {
    if (!isConfigured()) return;
    if (!hasConsent()) return;

    posthogService.initialize();
    posthog.capture("$pageview");
  },
};
